<template>
  <div class="custom__bg-gray">
    <div
      class="goal-callout-section__container"
      :class="{
        'goal-callout-section-header__without-objetives':
          isOpen && !value.peiProgramServiceObjective.length,
        'ai-field-highlighted': aiObjectivesModal.visible,
        'goal-callout-section__container--empty-objetives':
          showObjetivesSection && !value.peiProgramServiceObjective.length,
      }"
    >
      <div class="goal-callout-header__container">
        <div class="d-flex">
          <div
            class="goal-callout-header__icon"
            :class="{ 'goal-callout-header__icon-close': !isOpen }"
          >
            <span>Meta #{{ index }}</span>
            <p v-if="showObjetivesSection">
              <i
                v-if="isOpen"
                class="fas fa-chevron-circle-down"
                @click="toggleCallout"
              />
              <i
                v-else
                class="fas fa-chevron-circle-right"
                @click="toggleCallout"
              />
            </p>
          </div>
          <div class="goal-callout-header__text">
            <span v-if="!isEdit">{{ value.spaLongDescription }}</span>
            <ValidationObserver
              v-else
              style="width:100%"
              ref="formObserver"
              tag="form"
              novalidate
            >
              <validation-provider v-slot="{ errors }" rules="required">
                <CustomInput
                  v-model="descriptionTosave"
                  type="text"
                  :error="errors[0]"
                  :disabled="isReadOnly"
                  custom-error-msg="El campo es requerido."
                />
              </validation-provider>
            </ValidationObserver>

            <!-- reemplaza el panel "Añadir Objetivo" que antes se abría solo con 0
                 objetivos -- ahora solo se avisa, siempre visible (incluso con la Meta
                 colapsada), sin ocupar el espacio de un formulario completo. El panel real
                 se sigue abriendo únicamente con el clic explícito en "Añadir Objetivo"/
                 "Objetivo con Asistente IA" (goal-callout-body__actions más abajo), igual
                 que en una Meta que sí tiene objetivos. Va DENTRO de
                 goal-callout-header__text (misma columna que el texto de la Meta, debajo de
                 él) para que quede alineado con el texto -- no debajo del cuadro "Meta #N"
                 (por eso .goal-callout-header__text pasó a flex-direction: column). -->
            <div
              v-if="showObjetivesSection && !value.peiProgramServiceObjective.length"
              class="goal-callout-empty-objetives__flag"
            >
              <i class="fas fa-exclamation-triangle" /> Sin objetivos documentados
            </div>
          </div>
        </div>

        <div v-if="!isReadOnly" class="goal-callout-header__actions">
          <ActionButton
            v-if="isEdit"
            i="fa fa-times-circle ml-2"
            class="mr-2 button-size"
            variant="outline-danger"
            @click="isEdit = false"
          />
          <ActionButton
            v-else
            i="fa fa-times-circle ml-2"
            class="mr-2 button-size"
            variant="outline-danger"
            @click="deleteGoal"
          />
          <ActionButton
            v-if="!isEdit"
            i="fa fa-pencil ml-2"
            class="button-size"
            variant="success"
            @click="isEdit = true"
          />
          <ActionButton
            v-else
            i="fa fa-save ml-2"
            class="button-size"
            variant="success"
            @click="save"
          />
        </div>
      </div>

      <div
        v-if="
          showObjetivesSection &&
            isOpen &&
            !!value.peiProgramServiceObjective.length
        "
        class="goal-callout-body__container"
      >
        <p>
          <span class="objetive-title__chip">
            Objetivos
          </span>
        </p>

        <div
          v-for="(objetive, i) in value.peiProgramServiceObjective"
          class="goal-callout-body-objetive__container"
        >
          <ObjetiveItem
            :value="objetive"
            :main-index="index"
            :index="i + 1"
            :is-read-only="isReadOnly"
            @update-objetive="$emit('update-objetive', $event)"
            @delete-objetive="$emit('delete-objetive', $event)"
          />
        </div>
      </div>

      <div
        v-if="
          showObjetivesSection && isOpen && !showObjetiveForm && !isReadOnly
        "
        class="goal-callout-body__actions"
      >
        <!-- AG-05 -- "Objetivo con Asistente IA": gateado únicamente por aiAssistantEnabled
             (parámetro EnableAiAssistantPei, el mismo que ya usa Tab3.vue en III-A/III-B) --
             el gate del Diseñador de Metas y Objetivos v2 ya está implícito, porque este
             componente solo se renderiza dentro de ese flujo. Usa el violeta de Figma
             (nodo "Bot Icon", 6084:23752 en el mockup de la Tab II) -- antes usaba el
             mismo coral (#ff7058) que el encabezado "Meta #N" de este componente a
             propósito, mientras ese esquema de color no se adoptaba; ver AiAssistantModal.vue
             y AiSuggestionIcon.vue para el resto de piezas del asistente que también lo
             adoptaron. El encabezado "Meta #N" (línea ~557) no es parte del asistente de IA
             y se queda en coral. Versión compacta (Variante B del mockup aprobado por el
             usuario) -- ver el detalle técnico en el comentario de .ai-goal-trigger en
             GoalsManager.vue. -->
        <button
          v-if="aiAssistantEnabled"
          type="button"
          class="ai-objective-trigger mr-2"
          title="Objetivo con Asistente IA"
          @click="openAiObjectivesModal($event)"
        >
          <span class="ai-trigger-icon">
            <AiSparkIcon :size="16" variant="white" />
          </span>
          <span class="ai-trigger-label">Objetivo con Asistente IA</span>
        </button>
        <ActionButton
          i="fas fa-file-plus"
          class="mr-2"
          variant="outline-success"
          @click="handleAddObjetiveClick"
        >
          Añadir Objetivo
        </ActionButton>
      </div>
    </div>

    <Error
      v-if="isEmptySkills(value.strenghtsAndNecessities)"
      class="mx-4"
      message="Favor relacionar las necesidades a la meta."
    />

    <Error
      v-if="
        showObjetivesSection &&
          hasError &&
          value.peiProgramServiceObjective?.length <= 0 &&
          !isReadOnly
      "
      class="mx-4"
      message="Favor documentar los objetivos."
    />

    <div
      v-if="showObjetivesSection && showObjetiveForm && isOpen"
      class="goal-callout-section-objetive-form__container"
      :class="{
        'goal-callout-section-objetive-form__without-objetives':
          isOpen && !value.peiProgramServiceObjective.length,
        'goal-callout-section-objetive-form__container--ai': formOpenedViaAi,
      }"
    >
      <ObjetiveForm
        :ref="`newObjetiveForm-${customId}`"
        v-model="newObjetive"
        :adequacy-options="adequacyOptions"
        :instrument-options="instrumentOptions"
        @cancel="handleCancelObjetive"
        @save="handleSave"
      />
    </div>

    <AiAssistantModal
      :visible="aiObjectivesModal.visible"
      :loading="aiObjectivesModal.loading"
      :error="aiObjectivesModal.error"
      :anchor-rect="aiObjectivesModal.anchorRect"
      :best-practices="aiObjectivesModal.bestPractices"
      :can-refine="!!aiObjectivesModal.suggestions.length"
      refine-placeholder="Ej: más objetivos de comunicación, tono más sencillo..."
      :can-give-feedback="
        !!(
          aiObjectivesModal.suggestions.length &&
          aiObjectivesModal.aiGenerationLogId
        )
      "
      :feedback="aiObjectivesModal.feedback"
      :feedback-submitting="aiObjectivesModal.feedbackSubmitting"
      title="Objetivos Sugeridos por IA"
      :loading-text="aiObjectivesModal.loadingText"
      @close="closeAiObjectivesModal"
      @refine="handleRefine"
      @feedback="handleFeedback"
    >
      <!-- contexto: a qué Fortaleza/Necesidad, Sub Área y Meta puntual corresponden
           estos Objetivos sugeridos (mockup de Figma) -- se agrega SOLO acá, dentro del modal
           de IA; no reemplaza ni toca el encabezado coral "Meta #N" de la pantalla principal.
           la versión anterior de esto era una caja
           con borde/fondo violeta del mismo peso visual que las tarjetas de sugerencias de
           abajo -- se confundía con otra tarjeta más para elegir. Ahora es solo una línea
           liviana (sin caja, texto atenuado) con la pastilla chica como referencia, para que
           quede claro que es CONTEXTO y no otra opción para seleccionar. -->
      <div
        v-if="necessityText || subAreaText"
        class="ai-objectives-context__breadcrumb"
      >
        <span v-if="necessityText">
          Necesidad:
          <strong>{{ necessityText }}</strong>
        </span>
        <span v-if="subAreaText">
          Sub Área:
          <strong>{{ subAreaText }}</strong>
        </span>
      </div>
      <p class="ai-objectives-context__meta-line">
        <span class="ai-objectives-context__meta-badge">
          <AiSparkIcon :size="10" variant="white" />
          Meta #{{ index }}
        </span>
        {{ value.spaLongDescription }}
      </p>

      <div v-if="!aiObjectivesModal.suggestions.length" class="ai-modal-empty">
        {{ emptyObjectivesMessage }}
      </div>

      <div
        v-for="(suggestion, idx) in aiObjectivesModal.suggestions"
        :key="idx"
        class="ai-objective-suggestion"
      >
        <div class="ai-objective-suggestion__header">
          <div class="ai-objective-suggestion__header-left">
            <span class="ai-objective-suggestion__badge">
              <AiSparkIcon :size="13" variant="white" />
              Objetivo #{{ idx + 1 }}
            </span>
            <!-- "editar antes de usar" -- ver el comentario en aiSuggestionEditMixin.js.
                 A diferencia de la tarjeta de Meta, acá getDisplayText es 'concepto' (una
                 propiedad simple), no una función -- suggestion.concepto ES el texto real que se
                 guarda como description, no una vista compuesta. -->
            <span
              v-if="aiEditWasEdited(suggestion, 'concepto')"
              class="ai-objective-suggestion__edited-tag"
            >
              <i class="fa fa-pencil" />
              Editado
            </span>
          </div>
          <!-- botón único (ícono) para Editar/Cancelar -- ver el mismo comentario en
               GoalsManager.vue (íconos de FontAwesome, ya usados en el resto de la app, en vez
               de componentes SVG propios). -->
          <button
            type="button"
            class="ai-btn-icon"
            :class="{ 'ai-btn-icon--active': aiEditIsActive(suggestion) }"
            :title="aiEditIsActive(suggestion) ? 'Cancelar edición' : 'Editar'"
            @click="aiEditToggle(suggestion, 'concepto')"
          >
            <i v-if="aiEditIsActive(suggestion)" class="fas fa-times" />
            <i v-else class="fa fa-pencil" />
          </button>
        </div>
        <!-- la pastilla "Objetivo #N" queda sola arriba (ver header) para dar contexto
             primero; el checkbox flota (float: left) al lado de la 1ª línea del texto -- la
             sugerencia se lee primero, se selecciona después. Elegido sobre varios mockups de
             imagen que se le mostraron al usuario antes de tocar código. Solo las sugerencias
             COMPLETAS (con match real de Instrumento/Adecuación contra catálogo, ver
             isAiObjectiveSuggestionSelectable) son elegibles para inserción masiva -- las
             incompletas siguen requiriendo completarlas a mano vía "Usar este objetivo", y por
             eso no muestran checkbox. -->
        <div class="ai-objective-suggestion__content">
          <CustomCheckbox
            v-if="
              isAiObjectiveSuggestionSelectable(suggestion) &&
                !aiEditIsActive(suggestion)
            "
            v-model="suggestion._selected"
            title="Seleccionar"
            aria-label="Seleccionar"
            class="ai-objective-suggestion__checkbox"
          />
          <textarea
            v-if="aiEditIsActive(suggestion)"
            v-model="suggestion._aiEditText"
            class="ai-objective-suggestion__text-edit"
            rows="3"
          />
          <div v-else class="ai-objective-suggestion__text">
            {{ suggestion.concepto }}
          </div>
          <div class="ai-objective-suggestion__meta">
            <span>
              <strong>Instrumento:</strong>
              {{ suggestion.instrumentoNombre }}
            </span>
            <span>
              <strong>Medida/Adecuación:</strong>
              {{ suggestion.medidaNombre }}
            </span>
            <span v-if="formatAiCriterio(suggestion)">
              <strong>Criterio:</strong>
              {{ formatAiCriterio(suggestion) }}
            </span>
          </div>
          <div class="ai-objective-suggestion__actions">
            <button
              type="button"
              class="ai-btn ai-btn-primary"
              @click="handleAiObjectiveSelected(suggestion)"
            >
              <AiSparkIcon :size="13" variant="white" />
              Usar este objetivo
            </button>
          </div>
        </div>
      </div>

      <!-- inserción masiva: "Añadir seleccionados" guarda, en cadena, todos
           los objetivos marcados (reutilizando el mismo guardado que "Usar este objetivo"
           individual, uno por uno). Lógica y fila reusadas de aiBulkSelectMixin.js/
           AiBulkSelectBar.vue (ver esos archivos), las mismas que ya usan los modales de "Metas
           Sugeridas por IA". -->
      <AiBulkSelectBar
        :selectable-count="aiBulkSelectableSuggestions.length"
        :selected-count="aiBulkSelectedCount"
        :all-selected="aiBulkAllSelected"
        :disabled="aiObjectivesModal.loading"
        @toggle-select-all="aiBulkToggleSelectAll"
        @add-selected="aiBulkAddSelected"
      />

      <div class="ai-modal-actions">
        <button
          type="button"
          class="ai-btn ai-btn-outline"
          :disabled="aiObjectivesModal.loading"
          @click="requestAiObjectives"
        >
          <i class="fas fa-rotate-left" />
          Regenerar
        </button>
      </div>
    </AiAssistantModal>
  </div>
</template>

<script>
import ObjetiveForm from '../ObjetiveForm.vue';
import ActionButton from '@/components/common/ActionButton';
import CustomInput from 'CustomInput';
import { CustomCheckbox } from '@/components/common';
import alert from '@/mixin/alert.mixin';
import ObjetiveItem from './ObjetiveItem.vue';
import Error from '@/components/common/Error';
import store from '@/store/index';
import AiAssistantModal from '@/components/pei/shared/ai-assistant/AiAssistantModal.vue';
import AiSparkIcon from '@/components/pei/shared/ai-assistant/AiSparkIcon.vue';
import AiBulkSelectBar from '@/components/pei/shared/ai-assistant/AiBulkSelectBar.vue';
import aiBulkSelectMixin from '@/components/pei/shared/ai-assistant/aiBulkSelectMixin.js';
import aiSuggestionEditMixin from '@/components/pei/shared/ai-assistant/aiSuggestionEditMixin.js';
import GoalMeasures from '@/utils/constants/pei/peiGoalMeasures';
import {
  generateSuggestion,
  refineSuggestion,
  markSuggestionUsed,
  submitFeedback,
} from '@/services/pei/aiAssistant.js';

export default {
  name: 'SubAreaGoalCallout',
  mixins: [alert, aiBulkSelectMixin, aiSuggestionEditMixin],
  components: {
    ObjetiveForm,
    ActionButton,
    CustomInput,
    CustomCheckbox,
    ObjetiveItem,
    Error,
    AiAssistantModal,
    AiSparkIcon,
    AiBulkSelectBar,
  },
  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
    index: {
      type: Number,
      default: 0,
    },
    adequacyOptions: {
      type: Array,
      default: () => [],
    },
    instrumentOptions: {
      type: Array,
      default: () => [],
    },
    hasError: {
      type: Boolean,
      default: false,
    },
    skillsRequired: {
      type: Boolean,
      default: true,
    },
    isReadOnly: {
      type: Boolean,
      default: false,
    },
    customId: {
      type: String,
      default: 'sub-area-callout',
    },
    showObjetivesSection: {
      type: Boolean,
      default: true,
    },
    // AG-05 -- true solo cuando el sysparam EnableAiAssistantPei está activo (ver
    // AddProgramService.vue, mismo parámetro que usa Tab3.vue en III-A/III-B). Se pasa
    // como prop desde AddProgramService.vue a través de GoalsManager -> SubAreaContainer
    // -> SubAreaNecessity -> aquí, igual que adequacyOptions/instrumentOptions.
    aiAssistantEnabled: {
      type: Boolean,
      default: false,
    },
    autoOpenAiObjectivesGoalId: {
      type: Number,
      default: null,
    },

    suppressAutoOpenObjetiveForm: {
      type: Boolean,
      default: false,
    },
    necessityText: {
      type: String,
      default: null,
    },
    subAreaText: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      // Antes arrancaba en true -- por eso, aunque ya no se forzaba abrir el panel
      // "Añadir Objetivo" de una Meta vacía, la Meta SEGUÍA entrando expandida al tab
      // (chevron abierto, lista de Objetivos visible si ya tenía, botones de acción
      // visibles), acumulando scroll con cada Meta. Ahora todas entran colapsadas; la única
      // excepción es la Meta recién creada por "Meta con Asistente IA", que
      // maybeAutoOpenAiObjectives() abre explícitamente (ver más abajo).
      isOpen: false,
      showObjetiveForm: false,
      // true mientras "Añadir Objetivo" está abierto porque lo disparó una acción de IA
      // ("Usar este objetivo"/inserción masiva) -- controla el modificador de fondo violeta
      // suave (.goal-callout-section-objetive-form__container--ai) en el template. Con el
      // botón regular "Añadir Objetivo" queda en false y el formulario mantiene su estilo
      // original (mismo criterio que GoalsManager.vue/SubAreaNecessity.vue).
      formOpenedViaAi: false,
      descriptionTosave: null,
      isEdit: false,
      resources: [],
      newObjetive: {
        description: '',
        instrument: null,
        adequacy: null,
        measure1Amt: null,
        measure2Amt: null,
      },
      aiObjectivesModal: {
        visible: false,
        loading: false,
        // antes era un string fijo en el template ("Generando objetivos
        // sugeridos…"), ahora vive acá porque también se reutiliza (con otro texto)
        // mientras se guarda de verdad el objetivo elegido con "Usar este objetivo" (ver
        // handleAiObjectiveSelected) -- antes ese guardado corría totalmente invisible.
        loadingText: 'Generando objetivos sugeridos…',
        error: '',
        // Posición del botón que abrió el modal, para el diálogo "small" anclado (ver
        // Tab3.vue / AiAssistantModal.vue).
        anchorRect: null,
        // Array<{ concepto: String, instrumentoNombre: String, medidaNombre: String }>
        suggestions: [],
        // Id de LOG_AiGeneration de la corrida vigente -- hasta este cambio, AG-05 nunca
        // capturaba este dato (a diferencia de Tab3.vue, III-A/III-B), así que "Usar Esta
        // Versión" y el feedback 👍/👎 no se podían registrar aquí.
        aiGenerationLogId: null,
        feedback: null,
        feedbackSubmitting: false,
        // { title, items[], linkText?, linkUrl? } -- ver AiAssistantModal.vue (prop
        // best-practices). Viene del backend SI la configuración de V-Objetivos tiene
        // BestPracticesTitle/puntos cargados (Operaciones > Configuración de Prompts de IA);
        // si no, el backend devuelve null y el bloque simplemente no se pinta.
        bestPractices: null,
        // nivel de ejecución más reciente (ej. "L4 (Independiente)") de la destreza
        // vinculada a esta Meta, según el último informe de progreso (ver
        // PeiAIContextBuilderEngine.ResolveSelectedGoalAsync -> RecentLevel, dentro de
        // detectedContext, fuente "SelectedGoal"). Null si no hay informe/dato para esa
        // destreza. Se usa SOLO para explicarle al especialista, con el dato real, por qué tal
        // vez no aparecieron objetivos sugeridos (ver emptyObjectivesMessage más abajo) -- no es
        // una suposición nuestra, es lo mismo que vio el modelo.
        recentLevel: null,
      },
      // AG-05 -- "Meta + Objetivos encadenados": evita que maybeAutoOpenAiObjectives() vuelva
      // a abrir el modal si esta instancia recibe otro cambio de `value` (ej. al añadir un
      // objetivo) antes de que AddProgramService.vue termine de limpiar el prop de arriba.
      hasAutoOpenedAiObjectives: false,
    };
  },
  computed: {
    // adaptadores requeridos por aiBulkSelectMixin.js (ver el contrato
    // documentado ahí): exponen el modal/las sugerencias de ESTE componente bajo los nombres
    // fijos que el mixin espera.
    aiBulkSuggestions() {
      return this.aiObjectivesModal.suggestions;
    },
    aiBulkModalLoading: {
      get() {
        return this.aiObjectivesModal.loading;
      },
      set(value) {
        this.aiObjectivesModal.loading = value;
      },
    },
    aiBulkModalLoadingText: {
      get() {
        return this.aiObjectivesModal.loadingText;
      },
      set(value) {
        this.aiObjectivesModal.loadingText = value;
      },
    },
    aiBulkModalVisible: {
      get() {
        return this.aiObjectivesModal.visible;
      },
      set(value) {
        this.aiObjectivesModal.visible = value;
      },
    },
    aiBulkItemNoun() {
      return 'objetivo';
    },
    // antes esto era un texto fijo en el template ("No se generaron objetivos
    // sugeridos para esta meta. Intente nuevamente."), sin importar el motivo. Cuando SÍ
    // tenemos el nivel de ejecución más reciente de la destreza (ver aiObjectivesModal.
    // recentLevel), se lo mostramos al especialista -- la regla 7 del prompt V-Objetivos le
    // pide al modelo NO proponer objetivos nuevos si esa destreza ya está dominada, así que un
    // resultado vacío suele significar justamente eso, no un error. "Intente nuevamente" se
    // saca del mensaje en ese caso porque reintentar no va a cambiar el resultado -- lo que
    // cambia es el nivel real del estudiante.
    emptyObjectivesMessage() {
      if (this.aiObjectivesModal.recentLevel) {
        return `No se generaron objetivos nuevos para esta Meta: según el último informe de progreso, la destreza vinculada ya está en ${this.aiObjectivesModal.recentLevel}. Es posible que el Asistente la considere dominada. Podés revisar el progreso reciente o pedir "Regenerar" de todas formas.`;
      }

      return 'No se generaron objetivos sugeridos para esta meta. Intente nuevamente.';
    },
  },
  methods: {
    handleAddObjetiveClick() {
      this.showObjetiveForm = true;
      this.formOpenedViaAi = false;
    },
    handleCancelObjetive() {
      this.showObjetiveForm = false;
      this.formOpenedViaAi = false;

      const payload = {
        customId: this.customId,
        pending: false,
      };

      this.$emit('pending-objetive', payload);
    },
    isEmptySkills(skills) {
      return this.skillsRequired && this.hasError && skills?.length == 0;
    },
    async deleteGoal() {
      const goal = {
        peiProgramServiceGoalId: this.value.peiProgramServiceGoalId,
        spaLongDescription: this.descriptionTosave,
        spaShortDescription: this.descriptionTosave,
        peiProgramServiceObjective: this.value.peiProgramServiceObjective,
        programServiceAreaId: parseInt(
          this.$root.$route.query.programservicearea
        ),
        areaId: this.value.areaId,
        goalId: this.value.goalId,
        peiId: this.value.peiId,
        necessityId: this.value.necessityId,
        subAreaId: this.value.subAreaId,
      };

      let confirmDeletion = await this.ShowCancelModal(
        'Atención',
        `¿Está seguro que desea eliminar la meta: ${this.value.spaShortDescription}?`,
        'Si',
        'No'
      );

      if (!confirmDeletion) return;

      this.$emit('delete-goal', goal);
      this.isEdit = false;
    },
    async save() {
      const isValid = await this.$refs.formObserver.validate();
      if (!isValid) return;

      const goal = {
        peiProgramServiceGoalId: this.value.peiProgramServiceGoalId,
        spaLongDescription: this.descriptionTosave,
        spaShortDescription: this.descriptionTosave,
        peiProgramServiceObjective: this.value.peiProgramServiceObjective,
        programServiceAreaId: parseInt(
          this.$root.$route.query.programservicearea
        ),
        areaId: this.value.areaId,
        goalId: this.value.goalId,
        peiId: this.value.peiId,
        necessityId: this.value.necessityId,
        subAreaId: this.value.subAreaId,
      };

      this.$emit('update-goal', goal);
      this.isEdit = false;
    },
    resetState() {
      // bug encontrado: esto asignaba a `this.newGoal`, una propiedad que NO
      // existe en `data()` de este componente (la real es `newObjetive`, ver arriba). En
      // Vue 2 asignar una propiedad no declarada en `data()` no es reactivo y no dispara el
      // watcher de `newObjetive` -- por eso el modelo real (`newObjetive`) nunca se limpiaba
      // después de guardar, y el próximo "Añadir Objetivo" (manual o justo después de un
      // objetivo creado con el Asistente IA, que llena `newObjetive` en
      // handleAiObjectiveSelected) aparecía con los valores del objetivo anterior en vez de
      // en blanco.
      this.newObjetive = {
        description: '',
        instrument: null,
        adequacy: null,
        measure1Amt: null,
        measure2Amt: null,
      };
      this.showObjetiveForm = false;
      this.formOpenedViaAi = false;
    },
    toggleCallout() {
      this.isOpen = !this.isOpen;
    },
    handleSave(objetive) {
      const payload = {
        peiProgramServiceObjectivelId: 0,
        objectiveDescription: objetive.description,
        measure1Amt: objetive.measure1Amt,
        measure2Amt: objetive.measure2Amt,
        peiObjectiveMeasureCode: objetive.adequacyId,
        peiProgramServiceGoalId: parseInt(this.value.peiProgramServiceGoalId),
      };

      // si este guardado vino del auto-guardado de "Usar este objetivo" (ver
      // handleAiObjectiveSelected), hay un resolver esperando (this.$_resolveAiObjectiveSaved)
      // -- se lo pasamos a AddProgramService.handleAddObjetive como `onCreated` para que se
      // llame quando el guardado real (éxito o error) termine, y así el modal de IA sepa
      // cuándo apagar su loading. Un guardado manual normal no tiene resolver pendiente, así
      // que sigue emitiendo igual que antes.
      if (this.$_resolveAiObjectiveSaved) {
        const resolve = this.$_resolveAiObjectiveSaved;
        this.$_resolveAiObjectiveSaved = null;
        this.$emit('add-objetive', payload, resolve);
      } else {
        this.$emit('add-objetive', payload);
      }

      this.resetState();
    },
    async saveObjetive() {
      return await this.$refs[`newObjetiveForm-${this.customId}`].save();
    },
    // --- AG-05 -- "Objetivo con Asistente IA": propone Objetivos para ESTA Meta (fuente
    // MetaSeleccionada, CODE_AIPromptConfiguration Id 5 / SeccionArea "V-Objetivos"). El
    // disparador para el generador es la prioridad de esta iteración -- la sugerencia
    // elegida se inserta reutilizando el MISMO ObjetiveForm y flujo de guardado
    // ("add-objetive" -> AddProgramService.vue -> api/pei/AddProgramServiceObjective) que ya
    // usa "Añadir Objetivo" manual, en vez de duplicar esa lógica de guardado/validación. ---
    // AG-05 -- "Meta + Objetivos encadenados": dispara sola "Objetivo con Asistente IA"
    // cuando ESTA meta es la que acaba de crear "Meta con Asistente IA" -- se identifica por
    // el id real (peiProgramServiceGoalId), que AddProgramService.handleCreateGoal ya devuelve
    // en la respuesta del guardado. No dispara si el especialista ya abrió/cerró el modal
    // manualmente en esta instancia.
    maybeAutoOpenAiObjectives(newValue) {
      if (this.hasAutoOpenedAiObjectives) return;
      if (!this.aiAssistantEnabled || !this.autoOpenAiObjectivesGoalId) return;
      if (
        Number(newValue.peiProgramServiceGoalId) !==
        Number(this.autoOpenAiObjectivesGoalId)
      )
        return;

      this.hasAutoOpenedAiObjectives = true;
      // el watcher de `value` de abajo (immediate) ya corrió al montar esta
      // instancia -- CON autoOpenAiObjectivesGoalId todavía null, porque AddProgramService.
      // handleCreateGoal recién invoca el callback `onCreated` (el que fija
      // pendingAiChainGoalId) DESPUÉS de que el refetch de `goals` ya dejó esta Meta en
      // pantalla -- así que para una Meta de 0 objetivos, `showObjetiveForm` ya había
      // quedado en true (panel manual "Añadir Objetivo" abierto) antes de que este método
      // pudiera identificarla por id. Lo cerramos acá para que el especialista vea
      // únicamente el modal "Objetivos Sugeridos por IA", no los dos paneles a la vez.
      this.showObjetiveForm = false;
      // `isOpen` ahora arranca en false para TODAS las Metas (ver data()) -- esta es la
      // única que debe abrirse sola, para que el especialista la vea expandida (con el
      // modal de IA encima) sin tener que hacer clic en el chevron.
      this.isOpen = true;
      this.$emit('clear-ai-chain');
      this.$nextTick(() => {
        this.scrollIntoViewIfNeeded();
        this.openAiObjectivesModal();
      });
    },
    // "Meta + Objetivos encadenados" -- la Meta recién creada puede caer lejos del scroll
    // actual (se agrupa por Sub Área/Fortaleza-Necesidad, no siempre al final de la
    // página), así que sin esto el especialista ve el fondo violeta puesto pero tiene que
    // buscarla a mano. Solo se mueve si de verdad queda fuera de la parte visible -- si ya
    // se ve completa no hace nada.
    scrollIntoViewIfNeeded() {
      if (!this.$el?.getBoundingClientRect) return;

      const rect = this.$el.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const isFullyVisible = rect.top >= 0 && rect.bottom <= viewportHeight;

      if (isFullyVisible) return;

      this.$el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    },
    openAiObjectivesModal(event) {
      const rect = event?.currentTarget?.getBoundingClientRect();
      this.aiObjectivesModal.anchorRect = rect
        ? {
            top: rect.top,
            bottom: rect.bottom,
            left: rect.left,
            right: rect.right,
            width: rect.width,
            height: rect.height,
          }
        : null;
      this.aiObjectivesModal.visible = true;
      this.requestAiObjectives();
    },
    closeAiObjectivesModal() {
      this.aiObjectivesModal.visible = false;
    },
    async requestAiObjectives() {
      this.aiObjectivesModal.loading = true;
      // por si quedó en 'Guardando el objetivo seleccionado…' de un "Usar este
      // objetivo" previo (ver handleAiObjectiveSelected).
      this.aiObjectivesModal.loadingText = 'Generando objetivos sugeridos…';
      this.aiObjectivesModal.error = '';
      try {
        const { data } = await generateSuggestion({
          module: 'PEI',
          sectionArea: 'V-Objetivos',
          peiId: store.getters.SelectedPei,
          programServiceGoalId: parseInt(this.value.peiProgramServiceGoalId),
        });

        this.applyAiObjectivesResult(data);
      } catch (err) {
        this.aiObjectivesModal.error =
          err?.response?.data ||
          'No se pudieron generar objetivos sugeridos. Intente nuevamente.';
      } finally {
        this.aiObjectivesModal.loading = false;
      }
    },
    // Ya no hay wrapper local (submitRefine) -- el input de "Refinar Resultado" vive en
    // AiAssistantModal.vue, que emite 'refine' con la instrucción ya recortada.
    async handleRefine(instruccion) {
      if (!this.aiObjectivesModal.aiGenerationLogId) return;
      this.aiObjectivesModal.loading = true;
      this.aiObjectivesModal.error = '';
      try {
        const { data } = await refineSuggestion({
          aiGenerationLogId: this.aiObjectivesModal.aiGenerationLogId,
          teacherInstruction: instruccion,
        });

        this.applyAiObjectivesResult(data);
      } catch (err) {
        this.aiObjectivesModal.error =
          err?.response?.data ||
          'No se pudo refinar la sugerencia. Intente nuevamente.';
      } finally {
        this.aiObjectivesModal.loading = false;
      }
    },
    // Común a generate/refine -- se captura SIEMPRE, antes de revisar isBlocked, mismo orden
    // que Tab3.vue (applyAiResult), para que el Id de auditoría quede disponible incluso en
    // una corrida bloqueada/con error. Parsea la lista de objetivos sugeridos igual en ambos
    // casos (el backend devuelve la misma forma de respuesta para /generate y /refine).
    applyAiObjectivesResult(data) {
      this.aiObjectivesModal.aiGenerationLogId = data.aiGenerationLogId;
      this.aiObjectivesModal.feedback = null;
      this.aiObjectivesModal.feedbackSubmitting = false;
      // Se repite en cada respuesta (generate Y refine) -- mismo motivo que Tab3.vue: si no
      // se reasigna acá, la caja de "Buenas prácticas" desaparecería al refinar.
      this.aiObjectivesModal.bestPractices =
        data.bestPractices || this.aiObjectivesModal.bestPractices || null;

      // ver el comentario en aiObjectivesModal.recentLevel (data()). Se busca en
      // detectedContext (siempre viene, incluso si isBlocked) por SourceKey en vez de asumir
      // una posición fija en el array -- el orden de las fuentes no está garantizado.
      const selectedGoalSource = (data.detectedContext || []).find(
        (s) => s.sourceKey === 'SelectedGoal'
      );
      this.aiObjectivesModal.recentLevel =
        selectedGoalSource?.recentLevel || null;

      if (data.isBlocked) {
        this.aiObjectivesModal.error = data.blockedReason;
        this.aiObjectivesModal.suggestions = [];
        return;
      }

      let parsed = {};
      try {
        parsed = JSON.parse(data.suggestion);
      } catch (e) {
        parsed = {};
      }

      // `_selected` tageado a la CONSTRUCCIÓN (no con Vue.set/$set más
      // tarde) para que sea reactivo desde el arranque -- mismo patrón que applyAiGoalResult en
      // GoalsManager.vue/SubAreaNecessity.vue. Lo usan las casillas de selección masiva (ver
      // aiBulkSelectMixin.js).
      this.aiObjectivesModal.suggestions = (
        parsed.objetivos || []
      // el modelo puede devolver "concepto" partido en párrafos -- se aplana a texto corrido
      // acá, ANTES de guardarlo en la sugerencia (ver aiFlattenSuggestionText en
      // aiSuggestionEditMixin.js), porque applyAiObjectiveSuggestion usa suggestion.concepto
      // DIRECTO cuando no hubo edición, sin pasar por el mixin.
      ).map((o) => ({ ...o, concepto: this.aiFlattenSuggestionText(o.concepto), _selected: false }));
    },
    // Empareja instrumentoNombre/medidaNombre (texto libre devuelto por la IA, restringido
    // por EnumSourcesJson al catálogo real -- ver PeiAIEnumResolverEngine.ResolvePeiInstrumentoAsync/
    // ResolvePeiAdecuacionMedidaAsync) contra las opciones YA cargadas en pantalla, para que
    // el ObjetiveForm quede pre-llenado con el MISMO objeto {id, name}/{id, spaShortDescription}
    // que usaría una selección manual del dropdown. Si no hay match exacto (dato inconsistente
    // entre el momento en que se generó la sugerencia y el catálogo actual), queda en null y
    // el especialista lo selecciona manualmente -- la validación existente del formulario ya
    // lo exige, así que nunca se guarda un objetivo sin instrumento/adecuación válidos.
    // Muestra el criterio numérico que ahora trae la sugerencia (medidaValor1/medidaValor2,
    // ver CODE_AIPromptConfiguration Id 6 / V-Objetivos) igual que se vería en la
    // previsualización del objetivo ya guardado -- "8 de 10" para medida "Intentos", "80%"
    // para "Porcentual". Vacío si la corrida es de antes de este cambio (sin esos campos).
    formatAiCriterio(suggestion) {
      if (suggestion.medidaValor1 == null) return '';

      return suggestion.medidaValor2 != null
        ? `${suggestion.medidaValor1} de ${suggestion.medidaValor2}`
        : `${suggestion.medidaValor1}%`;
    },
    // Un objetivo está "completo" cuando trae TODO lo que ObjetiveForm.save() exige: el
    // instrumento y la medida emparejaron contra el catálogo real (si no hubo match exacto,
    // ver comentario arriba), y el valor numérico correspondiente vino en la sugerencia --
    // medidaValor1 siempre, medidaValor2 solo cuando la medida es "Intentos" (ver
    // GoalMeasures.attempts / ObjetiveForm.vue -> isAttempsAdequacy). Si algo de esto falta,
    // NO se autoguarda -- el formulario queda abierto para que el especialista lo complete o
    // corrija a mano, igual que hasta ahora.
    isAiObjectiveComplete(instrument, adequacy, suggestion) {
      if (!instrument || !adequacy || suggestion.medidaValor1 == null)
        return false;

      const isAttemptsMeasure = adequacy.id == GoalMeasures.attempts;

      return isAttemptsMeasure ? suggestion.medidaValor2 != null : true;
    },
    // extraído de handleAiObjectiveSelected para reusarlo también en la
    // inserción masiva (isAiObjectiveSuggestionSelectable/aiBulkSaveOne) sin duplicar el
    // criterio de match -- mismo patrón que matchAiGoalSuggestion en GoalsManager.vue/
    // SubAreaNecessity.vue.
    matchAiObjectiveSuggestion(suggestion) {
      const normalize = (text) => (text || '').trim().toLowerCase();

      const matchedInstrument =
        this.instrumentOptions.find(
          (x) => normalize(x.name) === normalize(suggestion.instrumentoNombre)
        ) || null;
      const matchedAdequacy =
        this.adequacyOptions.find(
          (x) =>
            normalize(x.spaShortDescription) ===
            normalize(suggestion.medidaNombre)
        ) || null;

      return { matchedInstrument, matchedAdequacy };
    },
    // Solo las sugerencias completas (ver isAiObjectiveComplete) son elegibles para la casilla
    // de selección masiva -- las incompletas siguen requiriendo completarlas a mano vía "Usar
    // este objetivo" (mismo criterio que isAiGoalSuggestionComplete en los modales de Metas).
    isAiObjectiveSuggestionSelectable(suggestion) {
      const {
        matchedInstrument,
        matchedAdequacy,
      } = this.matchAiObjectiveSuggestion(suggestion);
      return this.isAiObjectiveComplete(
        matchedInstrument,
        matchedAdequacy,
        suggestion
      );
    },
    // Adaptador de aiBulkSelectMixin.js -- ver el contrato documentado ahí.
    aiBulkIsSelectable(suggestion) {
      return this.isAiObjectiveSuggestionSelectable(suggestion);
    },
    aiBulkItemLabel(count) {
      return count === 1 ? 'objetivo sugerido' : 'objetivos sugeridos';
    },
    // lógica de guardado de UN objetivo, extraída de
    // handleAiObjectiveSelected para reusarla también en la inserción masiva (aiBulkSaveOne).
    // Reutiliza el MISMO ObjetiveForm/newObjetive de "Añadir Objetivo" (igual que antes) --
    // funciona en cadena porque cada llamada espera a que el guardado anterior termine del todo
    // (incl. resetState(), que corre al final de handleSave y deja showObjetiveForm en false --
    // por eso esta función lo reafirma en true más abajo, en CADA llamada, antes de guardar)
    // antes de que la siguiente sobreescriba newObjetive. Devuelve true si de verdad se guardó
    // (sugerencia completa), false si solo se dejó prellenado el form para completar a mano
    // (incompleta).
    async applyAiObjectiveSuggestion(suggestion) {
      const {
        matchedInstrument,
        matchedAdequacy,
      } = this.matchAiObjectiveSuggestion(suggestion);
      // "editar antes de usar" (ver aiSuggestionEditMixin.js): mismo criterio que
      // GoalsManager.vue/applyAiGoalSuggestion -- acá 'concepto' SÍ es el texto real que se
      // guarda (a diferencia de Metas, no es una vista compuesta), pero igual se salta
      // BuildObjetiveDescription cuando hubo edición (ver ObjetiveForm.vue-
      // >descriptionOverride) para que quede EXACTAMENTE lo que el especialista escribió.
      const wasEdited = this.aiEditHasEditedText(suggestion);

      this.newObjetive = {
        description: suggestion.concepto || '',
        instrument: matchedInstrument,
        adequacy: matchedAdequacy,
        measure1Amt: suggestion.medidaValor1 ?? null,
        measure2Amt: suggestion.medidaValor2 ?? null,
        descriptionOverride: wasEdited
          ? this.aiEditFinalText(suggestion, 'concepto')
          : null,
        descriptionAiGenerationLogId: wasEdited
          ? null
          : this.aiObjectivesModal.aiGenerationLogId,
      };

      // No bloqueante -- señal de auditoría/analítica (ver aiAssistant.js), igual que
      // handleUseVersion() en Tab3.vue. Nunca debe frenar que el especialista siga su flujo.
      if (this.aiObjectivesModal.aiGenerationLogId) {
        markSuggestionUsed({
          aiGenerationLogId: this.aiObjectivesModal.aiGenerationLogId,
        }).catch(() => {});
      }

      // resetState() (llamado dentro de handleSave, tras CADA guardado, incl. a
      // mitad de una inserción masiva) deja showObjetiveForm en false, lo que desmonta
      // <ObjetiveForm :ref="`newObjetiveForm-${customId}`"> (v-if="showObjetiveForm"). Sin
      // reafirmarlo acá en CADA llamada, el 2º objetivo del bulk en adelante fallaría al no
      // encontrar la ref -- mismo bug (y mismo arreglo) que forceShow en GoalsManager.vue.
      // formOpenedViaAi también se reafirma acá por el mismo motivo (el fondo violeta debe
      // seguir activo durante todo el bulk).
      this.showObjetiveForm = true;
      this.formOpenedViaAi = true;

      // AG-05 (guardado automático): si ya tenemos toda la información -- no hace falta
      // esperar a que el especialista solo confirme con "Guardar". $nextTick para que
      // ObjetiveForm (recién montado por showObjetiveForm/v-if) reciba newObjetive antes de
      // llamar a su save(); saveObjetive() sigue validando con las mismas reglas que un
      // guardado manual (rango del %, o que el numerador no sea mayor que el total de
      // intentos) -- si algo no pasa esa validación, el formulario simplemente queda abierto
      // con el error visible, en vez de guardar datos inválidos.
      if (
        this.isAiObjectiveComplete(
          matchedInstrument,
          matchedAdequacy,
          suggestion
        )
      ) {
        // antes esto cerraba el modal de inmediato (`aiObjectivesModal.visible =
        // false`, más abajo) y el guardado real (POST a AddProgramServiceObjective +
        // refetch, ver AddProgramService.handleAddObjetive) corría totalmente invisible --
        // el especialista no veía ningún indicador mientras tanto. Ahora dejamos el modal
        // abierto EN LOADING (mismo spinner que ya se usa al generar/refinar sugerencias)
        // hasta que el guardado de verdad termine, y recién ahí lo cerramos.
        this.aiObjectivesModal.loading = true;
        // En bulk, el loadingText por-item ("Guardando objetivo X de N…") ya lo puso
        // aiBulkAddSelected (aiBulkSelectMixin.js) -- no lo pisamos acá.
        if (!this.aiBulkSelectInProgress) {
          this.aiObjectivesModal.loadingText =
            'Guardando el objetivo seleccionado…';
        }

        // handleSave() resuelve esto vía el callback que ahora pasa como `onCreated` a
        // AddProgramService.handleAddObjetive -- señal real de que terminó (éxito O error;
        // ver el comentario en handleAddObjetive), no una espera arbitraria.
        const savedPromise = new Promise((resolve) => {
          this.$_resolveAiObjectiveSaved = resolve;
        });

        await this.$nextTick();
        await this.saveObjetive();
        await savedPromise;

        if (!this.aiBulkSelectInProgress) {
          this.aiObjectivesModal.loading = false;
        }

        return true;
      }

      return false;
    },
    async handleAiObjectiveSelected(suggestion) {
      await this.applyAiObjectiveSuggestion(suggestion);
      this.aiObjectivesModal.visible = false;
    },
    // Adaptador de aiBulkSelectMixin.js -- ver el contrato documentado ahí.
    async aiBulkSaveOne(suggestion) {
      return await this.applyAiObjectiveSuggestion(suggestion);
    },
    // Mismo patrón que Tab3.vue (handleFeedback): actualización optimista con reversión si
    // falla la llamada. "Usar este objetivo" y el feedback 👍/👎 son señales distintas -- un
    // especialista puede usar un objetivo mediocre por cansancio, o al revés.
    async handleFeedback(liked) {
      const logId = this.aiObjectivesModal.aiGenerationLogId;
      if (!logId || this.aiObjectivesModal.feedbackSubmitting) return;

      const previous = this.aiObjectivesModal.feedback;
      this.aiObjectivesModal.feedback = liked ? 'up' : 'down';
      this.aiObjectivesModal.feedbackSubmitting = true;

      try {
        await submitFeedback({ aiGenerationLogId: logId, liked });
      } catch (err) {
        this.aiObjectivesModal.feedback = previous;
        this.ShowToast(
          '',
          'No se pudo registrar tu respuesta. Intente nuevamente.',
          'error'
        );
      } finally {
        this.aiObjectivesModal.feedbackSubmitting = false;
      }
    },
  },
  watch: {
    value: {
      deep: true,
      immediate: true,
      handler(newValue) {
        if (!newValue) return;

        // Antes esta línea forzaba showObjetiveForm = true (panel manual "Añadir Objetivo"
        // abierto de una) apenas la Meta tenía 0 objetivos -- eso hacía que CUALQUIER Meta
        // vacía apareciera expandida con el formulario completo al entrar al tab, aunque el
        // especialista no hubiera pedido documentarla todavía. Ahora ese caso solo se avisa
        // con el indicador "Sin objetivos documentados" en el encabezado (ver
        // goal-callout-empty-objetives__flag más abajo) -- el panel de alta se abre recién
        // cuando el especialista hace clic en "Añadir Objetivo"/"Objetivo con Asistente IA",
        // igual que cualquier otra Meta.
        this.maybeAutoOpenAiObjectives(newValue);
      },
    },
    // AG-05 -- "Meta + Objetivos encadenados": el prop que de verdad llega tarde es ESTE, no
    // `value` -- ver el comentario largo dentro de maybeAutoOpenAiObjectives(). Sin este
    // watcher, cuando AddProgramService.vue por fin fija pendingAiChainGoalId (después del
    // refetch de `goals`, que es cuando esta instancia ya montó/actualizó con `value`), nada
    // volvía a llamar maybeAutoOpenAiObjectives() -- el id llegaba, pero el único lugar que lo
    // revisaba (el watcher de `value` de arriba) ya había corrido. El modal de "Objetivos
    // Sugeridos por IA" nunca se abría solo; quedaba únicamente el panel manual "Añadir
    // Objetivo" (abierto porque la Meta nueva todavía no tiene objetivos) -- exactamente el
    // síntoma reportado.
    autoOpenAiObjectivesGoalId(newGoalId) {
      if (newGoalId == null || !this.value) return;

      if (Number(this.value.peiProgramServiceGoalId) === Number(newGoalId)) {
        this.maybeAutoOpenAiObjectives(this.value);
        return;
      }

      // Este mismo prop llega a TODAS las Metas a la vez (ver SubAreaContainer.vue/
      // SubAreaNecessity.vue), no solo a la que acaba de crearse -- se aprovecha para que,
      // apenas aparece una Meta nueva vía "Meta con Asistente IA", cualquier OTRA Meta se
      // colapse sola -- tenga o no el panel manual "Añadir Objetivo" abierto (una Meta
      // abierta mostrando sus Objetivos ya guardados ocupa el mismo espacio de scroll que
      // una con el panel de alta abierto). Evita que la pantalla acumule contenido de otras
      // Metas mientras el especialista tiene que llegar hasta la Meta nueva para generar sus
      // objetivos.
      this.showObjetiveForm = false;
      this.isOpen = false;
    },
    newObjetive: {
      deep: true,
      handler(newValue) {
        const hasObjetiveChanges =
          !!newValue.description ||
          !!newValue.instrument ||
          !!newValue.adequacy ||
          !!newValue.measure1Amt ||
          !!newValue.measure2Amt;

        const payload = {
          customId: this.customId,
          pending: hasObjetiveChanges,
        };

        this.$emit('pending-objetive', payload);
      },
    },
  },
  mounted() {
    this.descriptionTosave = this.value.spaShortDescription;
  },
};
</script>

<style>
.goal-callout-section__container {
  padding: 20px;
  border-bottom-right-radius: 5px;
  border-bottom-left-radius: 5px;
}

/* franja + aviso para una Meta sin objetivos -- reemplaza el panel "Añadir Objetivo"
   forzado abierto que había antes. La franja se ve tenga la Meta colapsada o abierta. */
.goal-callout-section__container--empty-objetives {
  border-left: 3px solid #f5a623;
}

/* pastilla chica, alineada con el texto de la Meta (misma columna, debajo de él) -- no
   una banda que atraviese toda la tarjeta ni el cuadro naranja "Meta #N". */
.goal-callout-empty-objetives__flag {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 700;
  color: #8a5a0a;
  background-color: #fff8e1;
  border-radius: 6px;
  padding: 7px 12px;
  margin-top: 8px;
  width: fit-content;
}

.goal-callout-empty-objetives__flag i {
  font-size: 13px;
  color: #f5a623;
}

.goal-callout-section-objetive-form__container {
  padding: 20px 40px;
  border-radius: 5px;
}

/* fondo violeta suave (mismo tratamiento que .goal-new-form__container en
   GoalsManager.vue y .add-subarea-necessity__container--ai en SubAreaNecessity.vue) -- solo
   cuando el formulario se abrió por una acción de IA (formOpenedViaAi), NUNCA con "Añadir
   Objetivo" manual, que mantiene el contenedor de arriba tal cual. */
.goal-callout-section-objetive-form__container--ai {
  background-color: #faf8ff;
  border: 1.5px solid #ba65ff;
  border-radius: 10px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.goal-callout-section-header__without-objetives {
  margin: 0;
  border-bottom-right-radius: 0px;
  border-bottom-left-radius: 0px;
}

.goal-callout-section-objetive-form__without-objetives {
  margin-top: 0;
  border-top-right-radius: 0px;
  border-top-left-radius: 0px;
}

.goal-callout-header__container {
  display: flex;
  flex-wrap: wrap;
  background-color: white;
  justify-content: space-between;
  border-bottom: 2px #d9e5df solid;
}

.goal-callout-header__icon {
  width: 103px;
  font-size: 20px;
  background-color: #ff7058;
  border-radius: 5px 5px 0px 0px;
  color: #ffffff;
  padding-top: 20px;
  text-align: center;
}

.goal-callout-header__icon-close {
  background-color: #b5c3bc;
}

.goal-callout-header__text {
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-left: 15px;
  width: 50vw;
}

.goal-callout-header__actions {
  display: flex;
  align-items: center;
  padding-right: 15px;
}

.goal-callout-body__container {
  margin-top: 35px;
}

.objetive-title__chip {
  padding: 10px 16px;
  font-weight: bold;
  border-radius: 20px;
  background-color: #d3e0d9;
  border: 2px #aabbb4 solid;
  margin-bottom: 30px;
}

.goal-callout-body-objetive__container {
  padding: 10px 0;
  background-color: white;
  border-bottom: 1px solid #d9e5df;
}

.goal-callout-body__actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-top: 20px;
}

.button-size {
  height: 45px;
}

.custom__bg-gray {
  background-color: #f7f7f7 !important;
}

/* degradado violeta, tomado 1:1 del nodo "Bot Icon" del mockup de Figma (fileKey
   2Gbxwn31JzazuECLYZyoeS, nodeId 6511:24996).

   Versión compacta (Variante B del mockup HTML aprobado por el usuario) -- mismo detalle
   técnico que el comentario de .ai-goal-trigger en GoalsManager.vue: solo ícono en reposo
   (36px, max-width recortado), se expande a 260px en :hover/:focus-visible mostrando el texto
   con fade-in. */
.ai-objective-trigger {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 36px;
  padding: 0 9px;
  border: none;
  border-radius: 4px;
  background: linear-gradient(114.12deg, #5c58ed 24.303%, #ba65ff 134.64%);
  color: #fff;
  font-family: 'Lato', sans-serif;
  font-weight: 500;
  font-size: 15px;
  line-height: 1.4;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  max-width: 36px;
  transition: max-width 0.28s cubic-bezier(0.4, 0, 0.2, 1),
    background 0.15s ease-in-out;
}

.ai-objective-trigger:hover,
.ai-objective-trigger:focus-visible {
  max-width: 260px;
  background: linear-gradient(114.12deg, #4a45d6 24.303%, #a94dff 134.64%);
}

.ai-objective-trigger .ai-trigger-icon {
  flex: 0 0 16px;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ai-objective-trigger .ai-trigger-label {
  opacity: 0;
  transform: translateX(-4px);
  transition: opacity 0.18s ease 0.05s, transform 0.18s ease 0.05s;
}

.ai-objective-trigger:hover .ai-trigger-label,
.ai-objective-trigger:focus-visible .ai-trigger-label {
  opacity: 1;
  transform: translateX(0);
}

/* Resalta la Meta completa (border + fondo violeta, mismo esquema del asistente) mientras
   el modal de Objetivos Sugeridos por IA de ESTA meta está abierto -- "el campo donde fue
   disparado el asistente" para AG-05 es la Meta entera, no un input individual, porque el
   Orquestador sugiere objetivos para la meta como un todo. Selector compuesto (no una
   clase .ai-field-highlighted suelta) porque .goal-callout-section__container vive en el
   bloque <style> global de arriba, sin scoped. */
.goal-callout-section__container.ai-field-highlighted {
  border: 1px solid #ba65ff;
  background-color: #f8f5ff;
  transition: background-color 0.2s ease, border-color 0.2s ease;
}
</style>

<!--
  Estilos del CUERPO del AiAssistantModal (AG-05) -- en un <style scoped> aparte (el bloque
  de arriba es global, sin "scoped") porque ese contenido se pasa por slot: en Vue 2,
  <style scoped> del hijo (AiAssistantModal.vue) no alcanza al contenido que el padre le
  pasa por slot -- ese contenido se compila con el scope-id de ESTE archivo, no del hijo.
  Ponerlas acá en vez del bloque global de arriba evita que nombres genéricos como .ai-btn
  o .ai-modal-actions se filtren al resto de la app.
-->
<style scoped>
.ai-modal-empty {
  color: #11291b;
  font-family: 'Lato', sans-serif;
  font-size: 14px;
}

/* contexto ("Necesidad: X   Sub Área: Y" + pastilla "Meta #N") arriba de las
   sugerencias de Objetivos, mockup de Figma -- ver comentario en el <template>. */
.ai-objectives-context__breadcrumb {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 24px;
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: #4a5a53;
  margin: 0 0 10px 0;
}

.ai-objectives-context__breadcrumb strong {
  color: #11291b;
}

/* SIN caja/borde/fondo a propósito: la versión anterior (bordeada,
   fondo violeta) pesaba igual que las tarjetas de sugerencias de abajo y se confundía con
   otra opción para elegir. Esto es solo una línea de referencia -- por eso el texto va en gris
   atenuado (no negro como el resto del contenido) y la pastilla es más chica. */
.ai-objectives-context__meta-line {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 4px 8px;
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  line-height: 1.5;
  color: #6b7a74;
  margin: 0 0 16px 0;
}

.ai-objectives-context__meta-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex: none;
  padding: 3px 10px;
  border-radius: 50px;
  background: linear-gradient(104deg, #5c58ed 24.303%, #ba65ff 134.64%);
  color: #fff;
  font-family: 'Lato', sans-serif;
  font-weight: 700;
  font-size: 11px;
  white-space: nowrap;
}

/* tarjeta en columna: primero el header (pastilla "Objetivo #N" sola, da contexto),
   después el contenido (checkbox + texto + Instrumento/Adecuación + botón) -- ver
   .ai-objective-suggestion__header y .ai-objective-suggestion__content más abajo. */
.ai-objective-suggestion {
  display: flex;
  flex-direction: column;
  gap: 10px;
  /* Borde lila #c1c0f8 + fondo #f8f5ff -- mismo violeta sampleado de Figma que ya usan
     .ai-modal-suggestion-text (Tab3.vue) y .ai-field-highlighted de este archivo, en vez
     del gris verdoso #d9e5df anterior. */
  border: 1px solid #c1c0f8;
  border-radius: 4px;
  padding: 14px 16px;
  background-color: #f8f5ff;
}

/* contenido de la tarjeta (checkbox + texto + Instrumento/Adecuación + botón), DEBAJO
   de la pastilla. Es un bloque normal (no flex): el checkbox flota (float: left, ver
   .ai-objective-suggestion__checkbox) y el texto lo rodea -- la 1ª línea del párrafo empieza al
   lado del checkbox, las líneas siguientes (y Instrumento/Adecuación/botón, que llevan
   clear: both) caen a todo el ancho de la tarjeta. Reemplaza a .ai-objective-suggestion__body
   (columna flex) de la ronda anterior. */
.ai-objective-suggestion__content {
  overflow: hidden;
}

/* fila de encabezado de cada tarjeta sugerida: casilla de selección
   masiva (solo si el objetivo es "completo", ver isAiObjectiveSuggestionSelectable) + la
   pastilla "Objetivo #N" -- mismo patrón que .ai-goal-suggestion__header en
   GoalsManager.vue/SubAreaNecessity.vue (Metas). */
/* el checkbox se sacó de este header -- ahora la pastilla queda sola arriba, para dar
   contexto antes del checkbox (ver .ai-objective-suggestion__content). */
.ai-objective-suggestion__header {
  display: flex;
  align-items: center;
  /* ver el mismo comentario en GoalsManager.vue: separa la pastilla/etiqueta
     "Editado" del botón ícono de Editar. */
  justify-content: space-between;
}

/* ver el mismo comentario en GoalsManager.vue. */
.ai-objective-suggestion__header-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* "editar antes de usar" (ver aiSuggestionEditMixin.js) -- ver el mismo comentario en
   GoalsManager.vue. */
.ai-objective-suggestion__edited-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 4px;
  background: #eef0ef;
  color: #57685f;
  font-family: 'Lato', sans-serif;
  font-weight: 700;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

/* CustomCheckbox (@/components/common) -- mismo componente de checkbox que ya usa el resto del
   proyecto (ver ResultRow.vue, etc.) en vez del <input type="checkbox"> plano que tenía esto
   antes. El label que arma internamente CustomCheckbox vive en SU propio scope -- se estiliza
   acá con ::v-deep para que el texto "Seleccionar" siga el mismo tratamiento tipográfico que el
   resto de este modal. */
/* CustomCheckbox usa "input-group" como clase raíz (de Bootstrap, global), que trae
   width: 100% por defecto -- si no se anula, el checkbox flotado (float: left) se estira al
   ancho completo del contenedor en vez de medir solo su contenido. width: auto lo anula. El
   checkbox ahora flota a la izquierda del párrafo de texto (ver .ai-objective-suggestion__content)
   -- la 1ª línea del texto queda al lado del checkbox, las líneas siguientes caen a todo el
   ancho una vez que el texto supera su altura. */
.ai-objective-suggestion__checkbox {
  float: left;
  width: auto;
  margin: 2px 10px 6px 0;
}

.ai-objective-suggestion__checkbox ::v-deep label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: #4a5a53;
  cursor: pointer;
}

/* radio 4px (no píldora/50px) para que quede rectangular, igual que los botones
   ".ai-btn" (ej. "Usar este objetivo") -- pedido explícito del usuario. */
.ai-objective-suggestion__badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  align-self: flex-start;
  padding: 6px 14px;
  border-radius: 4px;
  background: linear-gradient(104deg, #5c58ed 24.303%, #ba65ff 134.64%);
  color: #fff;
  font-family: 'Lato', sans-serif;
  font-weight: 700;
  font-size: 13px;
}

.ai-objective-suggestion__text {
  margin: 0;
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  line-height: 1.6;
  color: #11291b;
  white-space: pre-wrap;
}

/* "editar antes de usar": mismo estilo que .ai-goal-suggestion__text-edit
   (GoalsManager.vue/SubAreaNecessity.vue) -- foco violeta consistente con
   .ai-modal-refine-input:focus. */
.ai-objective-suggestion__text-edit {
  display: block;
  width: 100%;
  margin: 0;
  padding: 8px 10px;
  border: 1px solid #a062fb;
  border-radius: 4px;
  outline: none;
  box-shadow: 0 0 0 3px rgba(92, 88, 237, 0.15);
  background: #fff;
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  line-height: 1.6;
  color: #11291b;
  resize: vertical;
}

/* clear: both asegura que Instrumento/Adecuación quede SIEMPRE debajo del checkbox
   flotado, sin importar cuántas líneas ocupe el texto de arriba. */
.ai-objective-suggestion__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 24px;
  margin-top: 10px;
  clear: both;
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: #4a5a53;
}

.ai-objective-suggestion__actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 8px;
}

.ai-modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.ai-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border-radius: 4px;
  padding: 7px 16px;
  font-family: 'Lato', sans-serif;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
}

.ai-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.ai-btn-outline {
  background: #fff;
  border: 1px solid #2db782;
  color: #2db782;
}

/* botón ícono de Editar/Cancelar -- mismo estilo .ai-btn-icon que GoalsManager.vue/
   SubAreaNecessity.vue -- gris neutro, a diferencia del .ai-btn-outline verde ya existente
   arriba (que es para otra acción). */
.ai-btn-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 4px;
  background: transparent;
  border: none;
  color: #57685f;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s ease-in-out, color 0.15s ease-in-out;
  /* tamaño del ícono FontAwesome (fa-pencil/fa-times) que va adentro -- antes lo daba el
     prop :size del componente SVG propio, ver el comentario del template. */
  font-size: 14px;
}

.ai-btn-icon:hover {
  background: #eef0ef;
}

/* Mientras se está editando (botón mostrando la "X" de Cancelar) -- mismo fondo que
   .ai-goal-suggestion__edited-tag para que se lea como "modo activo", no como error. */
.ai-btn-icon--active {
  background: #eef0ef;
  color: #11291b;
}

/* Violeta (mismo esquema del asistente -- ver AiAssistantModal.vue) para "Usar este
   objetivo", como en el mockup de Figma del panel docked -- a diferencia de
   .ai-btn-outline ("Regenerar"), que ahí se ve en verde y por eso no se tocó. */
.ai-btn-primary {
  background: linear-gradient(104deg, #5c58ed 24.303%, #ba65ff 134.64%);
  border: 1px solid #5c58ed;
  color: #fff;
}

.ai-btn-primary:hover:not(:disabled) {
  background: linear-gradient(104deg, #4a45d6 24.303%, #a94dff 134.64%);
}
</style>
